/**
 * useAssessmentLock — exclusive edit lock for draft assessments (FECOA-1302).
 *
 * On mount the hook acquires the lock for the current user, then refreshes it
 * on a heartbeat interval so it never goes stale while the tab is open. On
 * unmount (or tab close) it releases the lock.
 *
 * Fail-safe: the editor is read-only until this user is the *confirmed* lock
 * holder. If acquisition fails for any reason (another editor holds it, a
 * permission denial, or a server/network error) the caller stays read-only and
 * cannot save. This prevents the reversal bug where a swallowed acquire error
 * left a user editing without holding the lock, then flipped them to read-only
 * once someone else legitimately acquired it.
 *
 * Outcome is exposed as `reason`:
 *   - null           → this user owns the lock (editable)
 *   - 'initializing' → first acquire has not resolved yet (brief, read-only)
 *   - 'locked'       → another user holds an active lock (423)
 *   - 'denied'       → this user lacks edit permission (403). `holder`/`since`
 *                      are still populated when someone else is actively editing
 *                      so the "who is editing" banner shows to view-only users too.
 *   - 'error'        → server/network error; retrying, read-only until it clears
 *
 * Returns:
 *   - isReadOnly: true whenever this user is not the confirmed holder
 *   - isOwner:    true once this user holds the lock
 *   - holder:     { name, email } of the other editor when read-only ('locked')
 *   - since:      ISO timestamp the other editor acquired the lock
 *   - reason:     see above
 *   - enabled:    whether locking applies (draft only)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { acquireLock, releaseLock, getLockStatus } from '../../../../services/assessmentBuilderApi';

const DEFAULT_HEARTBEAT_MS = 45000;
const POLL_INTERVAL_MS = 15000;

export default function useAssessmentLock(assessmentUuid, { enabled = true } = {}) {
  const [isOwner, setIsOwner] = useState(false);
  const [holder, setHolder] = useState(null);
  const [since, setSince] = useState(null);
  const [reason, setReason] = useState('initializing');

  const heartbeatTimer = useRef(null);
  const pollTimer = useRef(null);
  const mountedRef = useRef(true);
  const heartbeatMsRef = useRef(DEFAULT_HEARTBEAT_MS);

  const clearTimers = useCallback(() => {
    if (heartbeatTimer.current) { clearInterval(heartbeatTimer.current); heartbeatTimer.current = null; }
    if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
  }, []);

  const applyOwned = useCallback((data) => {
    if (!mountedRef.current) return;
    setIsOwner(true);
    setHolder(null);
    setSince(null);
    setReason(null);
    if (data?.heartbeat_interval_seconds) {
      heartbeatMsRef.current = data.heartbeat_interval_seconds * 1000;
    }
  }, []);

  const applyLockedByOther = useCallback((lock) => {
    if (!mountedRef.current) return;
    setIsOwner(false);
    setHolder(lock?.locked_by || null);
    setSince(lock?.acquired_at || null);
    setReason('locked');
  }, []);

  // Permission-denied viewer. Even though this user can never edit, we still
  // surface who is actively editing (AC-3 visibility) by passing the current
  // lock through when one exists — the banner shows the holder + since. When no
  // one is editing, the lock is null and the banner falls back to the plain
  // "no edit access" message.
  const applyDenied = useCallback((lock = null) => {
    if (!mountedRef.current) return;
    setIsOwner(false);
    setHolder(lock?.locked_by || null);
    setSince(lock?.acquired_at || null);
    setReason('denied');
  }, []);

  // Read-only status poll for permission-denied viewers: never tries to acquire
  // (that would just 403 again), only refreshes who is editing so the holder
  // banner appears/clears live as others start and stop.
  const startViewerPolling = useCallback(() => {
    if (pollTimer.current) return;
    pollTimer.current = setInterval(async () => {
      try {
        const status = await getLockStatus(assessmentUuid);
        applyDenied(status?.locked && !status.locked_by_me ? status : null);
      } catch {
        /* transient — keep the last state */
      }
    }, POLL_INTERVAL_MS);
  }, [assessmentUuid, applyDenied]);

  // Try to take (or refresh) the lock. Resolves to one of:
  //   'owned' | 'locked' | 'denied' | 'error'.
  // The 'error' branch is intentionally non-destructive: it does NOT change
  // ownership, so a transient heartbeat blip keeps a confirmed owner editing,
  // while a failed *initial* acquire leaves the caller read-only (isOwner is
  // still false from mount) rather than silently editable.
  const tryAcquire = useCallback(async () => {
    try {
      const data = await acquireLock(assessmentUuid);
      // Non-draft assessments are not lockable; treat as owned (read-only is
      // governed by the review workflow, not this hook).
      applyOwned(data);
      return 'owned';
    } catch (err) {
      const status = err?.response?.status;
      if (status === 423) {
        applyLockedByOther(err.response.data || {});
        return 'locked';
      }
      if (status === 403) {
        applyDenied();
        return 'denied';
      }
      // Server/network error: fail safe. Leave ownership untouched and let the
      // caller keep the editor read-only + retry.
      return 'error';
    }
  }, [assessmentUuid, applyOwned, applyLockedByOther, applyDenied]);

  // Poll while we are not the holder (locked by someone else, or a transient
  // error), and re-acquire as soon as it frees up.
  const startPolling = useCallback(() => {
    if (pollTimer.current) return;
    pollTimer.current = setInterval(async () => {
      try {
        const status = await getLockStatus(assessmentUuid);
        if (!status?.locked) {
          // Free or stale — grab it and stop polling on success.
          const outcome = await tryAcquire();
          if (outcome === 'owned') {
            clearInterval(pollTimer.current);
            pollTimer.current = null;
          }
        } else {
          applyLockedByOther(status);
        }
      } catch {
        /* transient — keep polling, stay read-only */
      }
    }, POLL_INTERVAL_MS);
  }, [assessmentUuid, tryAcquire, applyLockedByOther]);

  const startHeartbeat = useCallback(() => {
    if (heartbeatTimer.current) return;
    heartbeatTimer.current = setInterval(async () => {
      const outcome = await tryAcquire();
      // Only give up ownership on a *definitive* loss (taken over, or perms
      // revoked). A transient 'error' keeps us the owner — the 120s server-side
      // TTL protects us, and the next heartbeat reconciles.
      if (outcome === 'locked' || outcome === 'denied') {
        if (heartbeatTimer.current) { clearInterval(heartbeatTimer.current); heartbeatTimer.current = null; }
        if (outcome === 'locked') startPolling();
        // Permission revoked mid-session (rare): drop to read-only viewer polling
        // so this user now sees who is editing rather than a bare denial.
        else startViewerPolling();
      }
    }, heartbeatMsRef.current);
  }, [tryAcquire, startPolling, startViewerPolling]);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled || !assessmentUuid) return undefined;

    // Reset to a safe (read-only) starting point whenever the target changes.
    setIsOwner(false);
    setHolder(null);
    setSince(null);
    setReason('initializing');

    let cancelled = false;
    (async () => {
      const outcome = await tryAcquire();
      if (cancelled || !mountedRef.current) return;
      if (outcome === 'owned') {
        startHeartbeat();
      } else if (outcome === 'denied') {
        // No edit permission this session, but still show who is editing if the
        // draft is actively locked (AC-3). Read the current status once, then
        // poll read-only so the holder banner tracks live without re-acquiring.
        const status = await getLockStatus(assessmentUuid).catch(() => null);
        if (!cancelled && mountedRef.current) {
          applyDenied(status?.locked && !status.locked_by_me ? status : null);
        }
        startViewerPolling();
      } else {
        // 'locked' or 'error' — poll and re-acquire when it frees up. On a
        // server/network error the reason is still 'initializing', so surface
        // the retry state explicitly.
        if (outcome === 'error') setReason('error');
        startPolling();
      }
    })();

    // Best-effort release if the tab is closed/refreshed.
    const handleUnload = () => {
      try {
        const url = `/api/assessment-builder/assessments/${assessmentUuid}/release-lock/`;
        const token = localStorage.getItem('authToken');
        if (navigator.sendBeacon && token) {
          // sendBeacon cannot set Authorization headers; fall back to a keepalive
          // fetch so the release carries the bearer token.
          fetch(url, { method: 'POST', keepalive: true, headers: { Authorization: `Bearer ${token}` } });
        }
      } catch { /* ignore */ }
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      cancelled = true;
      mountedRef.current = false;
      clearTimers();
      window.removeEventListener('beforeunload', handleUnload);
      // Release on normal unmount (navigating away within the SPA).
      releaseLock(assessmentUuid).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentUuid, enabled]);

  return {
    isReadOnly: enabled && !isOwner,
    isOwner,
    holder,
    since,
    reason,
    enabled,
  };
}
